// =======================================================================
// PDF Export — Wave 4.5. Reuses the real CostSpace pattern discovered in
// PrintDocument.jsx: window.open() + an HTML document styled for A4 print
// + window.print(). No jsPDF/pdf-lib introduced, per the established
// architecture. Every number is read verbatim from the calculation
// chain — no computation happens in this file.
// =======================================================================
import { supabase } from './supabase'

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']

function threeDigitsToWords(n) {
  let s = ''
  if (n >= 100) { s += ONES[Math.floor(n / 100)] + ' Hundred '; n %= 100 }
  if (n >= 20) { s += TENS[Math.floor(n / 10)] + ' '; n %= 10 }
  if (n > 0) s += ONES[n] + ' '
  return s.trim()
}
function numberToWords(num) {
  num = Math.floor(Math.abs(num))
  if (num === 0) return 'Zero'
  const groups = ['', 'Thousand', 'Million', 'Billion']
  let parts = []
  let i = 0
  while (num > 0) {
    const chunk = num % 1000
    if (chunk > 0) parts.unshift(threeDigitsToWords(chunk) + (groups[i] ? ' ' + groups[i] : ''))
    num = Math.floor(num / 1000)
    i++
  }
  return parts.join(' ').replace(/\s+/g, ' ').trim()
}
// Reused verbatim from the existing CostSpace PrintDocument.jsx convention
// (SAR whole units + Halalas fractional), so the wording matches other
// CostSpace-generated documents exactly.
function amountInWords(amount, currency = 'SAR') {
  const whole = Math.floor(amount)
  const fils = Math.round((amount - whole) * 100)
  let s = numberToWords(whole) + ' ' + (currency === 'SAR' ? 'Saudi Riyals' : currency)
  if (fils > 0) s += ' And ' + numberToWords(fils) + ' Halalas'
  return s + ' Only'
}

const fmt = (n) => (n || n === 0) ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'

const STATUS_LABEL = {
  priced: { label: 'Priced', bg: '#F0FDF4', tx: '#166534' },
  unpriced: { label: 'Unpriced', bg: '#FEF2F2', tx: '#991B1B' },
  incomplete: { label: 'Incomplete', bg: '#FFF7ED', tx: '#9A3412' },
  zero_priced: { label: 'Zero Priced', bg: '#FFFBEB', tx: '#92400E' },
  excluded: { label: 'Excluded', bg: '#F3F4F6', tx: '#6B7280' },
}

const BASE_STYLE = `
  @page { size: A4; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #0F1017; margin: 0; font-size: 11px; }
  .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #1B3A6B; padding-bottom: 10px; margin-bottom: 16px; }
  .brand { font-size: 18px; font-weight: 800; color: #1B3A6B; }
  .doc-title { font-size: 14px; font-weight: 700; color: #0F1017; margin-top: 2px; }
  .meta { text-align: right; font-size: 10px; color: #6B7280; line-height: 1.6; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 24px; margin-bottom: 18px; font-size: 11px; }
  .info-row { display: flex; justify-content: space-between; border-bottom: 1px dotted #E5E7EB; padding: 3px 0; }
  .info-label { color: #6B7280; }
  .info-value { font-weight: 600; }
  .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #6B7280; margin: 18px 0 8px; border-bottom: 1px solid #E5E7EB; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  th { background: #1B3A6B; color: #fff; text-align: left; padding: 7px 8px; font-size: 9.5px; text-transform: uppercase; letter-spacing: 0.03em; }
  td { padding: 6px 8px; border-bottom: 1px solid #E5E7EB; }
  tr:nth-child(even) td { background: #F9FAFB; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .total-row td { background: #1B3A6B !important; color: #fff; font-weight: 700; }
  .tender-price-box { margin-top: 20px; padding: 16px 20px; background: #F8FAFF; border: 2px solid #1B3A6B; border-radius: 6px; }
  .tender-price-label { font-size: 11px; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; }
  .tender-price-value { font-size: 26px; font-weight: 800; color: #1B3A6B; margin-top: 4px; }
  .amount-words { font-size: 10.5px; color: #6B7280; margin-top: 6px; font-style: italic; }
  .status-badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 9px; font-weight: 700; }
  .footer { position: fixed; bottom: 8mm; left: 16mm; right: 16mm; font-size: 9px; color: #9CA3AF; display: flex; justify-content: space-between; border-top: 1px solid #E5E7EB; padding-top: 4px; }
`

async function fetchPdfData(tenderId, revisionId, { includeFullyLoaded = false } = {}) {
  const [tenderRes, revRes] = await Promise.all([
    supabase.from('tender').select('name, client_name, location').eq('id', tenderId).single(),
    supabase.from('estimate_revision').select('revision_number, label, status, pricing_date').eq('id', revisionId).single(),
  ])
  if (tenderRes.error) throw new Error(tenderRes.error.message)
  if (revRes.error) throw new Error(revRes.error.message)

  if (revRes.data?.status === 'frozen') {
    // Frozen: estimate_revision_calculation_chain / boq_item_pricing_status
    // / boq_item_selling_price / boq_item_fully_loaded_pricing all return
    // empty once the live source tables are cleared post-freeze (proven
    // directly). Build the same chain shape from freeze_snapshot instead,
    // mirroring TenderSummaryPage's own proven buildFrozenChain: every
    // field below is either a direct snapshot column or a straight
    // sum/subtract over full_boq_data / full_indirect_data already
    // captured verbatim at freeze time -- no new financial formula.
    const snapRes = await supabase
      .from('freeze_snapshot')
      .select('direct_cost_total, ho_overhead_total, markup_total, tender_price_total, full_boq_data, full_indirect_data')
      .eq('estimate_revision_id', revisionId)
      .maybeSingle()
    if (snapRes.error) throw new Error(snapRes.error.message)
    if (!snapRes.data) throw new Error('This revision is marked frozen but no snapshot record could be found.')

    const boqData = Array.isArray(snapRes.data.full_boq_data) ? snapRes.data.full_boq_data : []
    const indirect = snapRes.data.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
    const pkgCosts = Array.isArray(indirect.package_specific_costs) ? indirect.package_specific_costs : []

    let boqSiteOverhead = 0, boqRisk = 0
    boqData.forEach((it) => {
      boqSiteOverhead += Number(it.boq_specific_site_overhead) || 0
      boqRisk += Number(it.boq_specific_risk) || 0
    })
    let pkgSiteOverhead = 0, pkgRisk = 0
    pkgCosts.forEach((p) => {
      pkgSiteOverhead += Number(p.package_specific_site_overhead) || 0
      pkgRisk += Number(p.package_specific_risk) || 0
    })

    const chain = {
      direct_cost_total: snapRes.data.direct_cost_total,
      boq_specific_site_overhead_total: boqSiteOverhead,
      boq_specific_risk_total: boqRisk,
      package_specific_site_overhead_total: pkgSiteOverhead,
      package_specific_risk_total: pkgRisk,
      general_site_overhead_total: Number(indirect.general_site_overhead_total) || 0,
      ho_overhead_total: snapRes.data.ho_overhead_total,
      general_contingency_total: Number(indirect.general_contingency_total) || 0,
      markup_total: snapRes.data.markup_total,
      tender_price_total: snapRes.data.tender_price_total,
    }

    let items = []
    if (includeFullyLoaded) {
      // Same presentation-level allocation already proven exact in
      // RevisionWorkspacePage.jsx's buildFrozenFullyLoadedMap -- replayed
      // here over the same immutable inputs.
      const eligibleTotal = boqData.reduce((s, it) => { const dc = Number(it.direct_cost) || 0; return dc > 0 ? s + dc : s }, 0)
      const gso = chain.general_site_overhead_total, ho = chain.ho_overhead_total, gc = chain.general_contingency_total
      items = boqData.map((it) => {
        const dc = Number(it.direct_cost) || 0
        const allocPct = (dc > 0 && eligibleTotal > 0) ? dc / eligibleTotal : 0
        const allocatedGso = allocPct * gso, allocatedHo = allocPct * ho, allocatedGc = allocPct * gc
        const fullyLoadedCost = dc + (Number(it.boq_specific_site_overhead) || 0) + (Number(it.boq_specific_risk) || 0) + allocatedGso + allocatedHo + allocatedGc
        const markupPct = Number(it.effective_markup_pct) || 0
        return {
          boq_item_id: it.id, item_no: it.item_no, description: it.description, unit: it.unit,
          resolved_quantity: it.resolved_quantity, is_excluded: it.is_excluded, pricing_status: it.is_excluded ? 'excluded' : null,
          direct_cost: dc,
          fullyLoaded: { allocated_general_site_overhead: allocatedGso, allocated_ho_overhead: allocatedHo, allocated_general_contingency: allocatedGc, fully_loaded_cost: fullyLoadedCost, fully_loaded_selling_price: fullyLoadedCost * (1 + markupPct / 100) },
        }
      })
    } else {
      items = boqData.map((it) => ({
        boq_item_id: it.id, item_no: it.item_no, description: it.description, unit: it.unit,
        resolved_quantity: it.resolved_quantity, is_excluded: it.is_excluded, pricing_status: it.is_excluded ? 'excluded' : null,
        direct_cost: it.direct_cost, fullyLoaded: null,
      }))
    }

    return { tender: tenderRes.data, revision: revRes.data, chain, items }
  }

  const queries = [
    supabase.from('estimate_revision_calculation_chain').select('*').eq('estimate_revision_id', revisionId).maybeSingle(),
    supabase.from('boq_item_pricing_status').select('*').eq('estimate_revision_id', revisionId).order('item_no'),
    supabase.from('boq_item_selling_price').select('boq_item_id, direct_cost').eq('estimate_revision_id', revisionId),
  ]
  // Phase 1 — Derived Fully Loaded Pricing, only fetched for the BOQ
  // Detail export (Tender Summary export is untouched). Read verbatim
  // from the approved read-only view; no allocation math here.
  if (includeFullyLoaded) {
    queries.push(supabase.from('boq_item_fully_loaded_pricing')
      .select('boq_item_id, allocated_general_site_overhead, allocated_ho_overhead, allocated_general_contingency, fully_loaded_cost, fully_loaded_selling_price')
      .eq('estimate_revision_id', revisionId))
  }
  const results = await Promise.all(queries)
  const [chainRes, pricingRes, sellingRes, fullyLoadedRes] = results
  if (chainRes.error) throw new Error(chainRes.error.message)
  if (pricingRes.error) throw new Error(pricingRes.error.message)
  if (sellingRes.error) throw new Error(sellingRes.error.message)
  if (fullyLoadedRes?.error) throw new Error(fullyLoadedRes.error.message)

  const directCostMap = Object.fromEntries((sellingRes.data || []).map((s) => [s.boq_item_id, s.direct_cost]))
  const fullyLoadedMap = Object.fromEntries((fullyLoadedRes?.data || []).map((f) => [f.boq_item_id, f]))
  const items = (pricingRes.data || []).map((it) => ({
    ...it,
    direct_cost: directCostMap[it.boq_item_id] ?? null,
    fullyLoaded: fullyLoadedMap[it.boq_item_id] || null,
  }))

  return { tender: tenderRes.data, revision: revRes.data, chain: chainRes.data, items }
}

function openPrintWindow(html) {
  const win = window.open('', '_blank')
  if (!win) { alert('Please allow pop-ups to generate the PDF.'); return }
  win.document.write(html)
  win.document.close()
  win.onload = () => { win.focus(); win.print() }
}

export async function exportTenderSummaryPdf(tenderId, revisionId) {
  const { tender, revision, chain } = await fetchPdfData(tenderId, revisionId, { includeFullyLoaded: false })
  const nowStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  if (!chain) {
    openPrintWindow(`<html><head><style>${BASE_STYLE}</style></head><body>
      <div class="header"><div><div class="brand">CostSpace Estimation</div><div class="doc-title">Tender Summary</div></div></div>
      <p style="color:#991B1B; margin-top: 40px;">No calculation data is available for this revision yet.</p>
    </body></html>`)
    return
  }

  const siteOh = Number(chain.general_site_overhead_total) + Number(chain.boq_specific_site_overhead_total) + Number(chain.package_specific_site_overhead_total)
  const contingency = Number(chain.general_contingency_total) + Number(chain.boq_specific_risk_total) + Number(chain.package_specific_risk_total)

  const html = `<html><head><title>Tender Summary — ${tender.name}</title><style>${BASE_STYLE}</style></head><body>
    <div class="header">
      <div><div class="brand">CostSpace Estimation</div><div class="doc-title">Tender Summary Report</div></div>
      <div class="meta">Generated ${nowStr}<br/>Revision ${revision.revision_number}</div>
    </div>

    <div class="info-grid">
      <div class="info-row"><span class="info-label">Project / Tender</span><span class="info-value">${tender.name}</span></div>
      <div class="info-row"><span class="info-label">Client</span><span class="info-value">${tender.client_name || '—'}</span></div>
      <div class="info-row"><span class="info-label">Revision</span><span class="info-value">Rev ${revision.revision_number} — ${revision.label}</span></div>
      <div class="info-row"><span class="info-label">Status</span><span class="info-value">${revision.status}</span></div>
      <div class="info-row"><span class="info-label">Pricing Date</span><span class="info-value">${revision.pricing_date}</span></div>
      <div class="info-row"><span class="info-label">Location</span><span class="info-value">${tender.location || '—'}</span></div>
    </div>

    <div class="section-title">Financial Summary</div>
    <table>
      <tr><td>Direct Cost</td><td class="num">${fmt(chain.direct_cost_total)} SAR</td></tr>
      <tr><td>General Site Overhead</td><td class="num">${fmt(siteOh)} SAR</td></tr>
      <tr><td>Head Office Overhead</td><td class="num">${fmt(chain.ho_overhead_total)} SAR</td></tr>
      <tr><td>Contingency</td><td class="num">${fmt(contingency)} SAR</td></tr>
      <tr><td>Markup</td><td class="num">${fmt(chain.markup_total)} SAR</td></tr>
    </table>

    <div class="tender-price-box">
      <div class="tender-price-label">Tender Price</div>
      <div class="tender-price-value">${fmt(chain.tender_price_total)} SAR</div>
      <div class="amount-words">${amountInWords(Number(chain.tender_price_total))}</div>
    </div>

    <div class="footer"><span>CostSpace Estimation — Confidential</span><span>Page 1</span></div>
  </body></html>`

  openPrintWindow(html)
}

export async function exportBoqDetailPdf(tenderId, revisionId) {
  const { tender, revision, items } = await fetchPdfData(tenderId, revisionId, { includeFullyLoaded: true })
  const nowStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })

  let totalDirect = 0
  let totalSelling = 0
  let totalAllocatedIndirect = 0
  let totalFullyLoadedCost = 0
  let totalFullyLoadedSelling = 0
  const rowsHtml = items.map((it) => {
    const cfg = STATUS_LABEL[it.pricing_status] || STATUS_LABEL.unpriced
    const qty = Number(it.resolved_quantity) || 0
    const dc = it.pricing_status === 'excluded' ? null : (it.direct_cost ?? 0)
    const sp = it.pricing_status === 'excluded' ? null : (it.selling_price ?? 0)
    // Phase 1 -- derived, read verbatim from boq_item_fully_loaded_pricing.
    // Separate from the official Direct Cost / Direct + Markup Price above.
    const allocIndirect = it.pricing_status === 'excluded' ? null :
      (Number(it.fullyLoaded?.allocated_general_site_overhead) || 0)
      + (Number(it.fullyLoaded?.allocated_ho_overhead) || 0)
      + (Number(it.fullyLoaded?.allocated_general_contingency) || 0)
    const flc = it.pricing_status === 'excluded' ? null : (Number(it.fullyLoaded?.fully_loaded_cost) || 0)
    const fls = it.pricing_status === 'excluded' ? null : (Number(it.fullyLoaded?.fully_loaded_selling_price) || 0)
    // Final Unit Rate -- derived from the FINAL price (Fully Loaded
    // Selling Price), never from Direct + Markup Price, matching the
    // BOQ screen's corrected business decision. NULL for qty=0.
    const finalUnitRate = (fls != null && qty > 0) ? fls / qty : null
    if (dc != null) totalDirect += Number(dc)
    if (sp != null) totalSelling += Number(sp)
    if (allocIndirect != null) totalAllocatedIndirect += allocIndirect
    if (flc != null) totalFullyLoadedCost += flc
    if (fls != null) totalFullyLoadedSelling += fls
    return `<tr>
      <td>${it.item_no || '—'}</td>
      <td>${it.description}</td>
      <td>${it.unit}</td>
      <td class="num">${fmt(qty)}</td>
      <td><span class="status-badge" style="background:${cfg.bg};color:${cfg.tx}">${cfg.label}</span></td>
      <td class="num">${dc != null ? fmt(dc) : '—'}</td>
      <td class="num">${sp != null ? fmt(sp) : '—'}</td>
      <td class="num" style="background:#FFFBF5">${allocIndirect != null ? fmt(allocIndirect) : '—'}</td>
      <td class="num" style="background:#FFFBF5">${flc != null ? fmt(flc) : '—'}</td>
      <td class="num" style="background:#FFFBF5">${fls != null ? fmt(fls) : '—'}</td>
      <td class="num" style="background:#FFFBF5">${finalUnitRate != null ? fmt(finalUnitRate) : '—'}</td>
    </tr>`
  }).join('')

  const html = `<html><head><title>BOQ Detail — ${tender.name}</title><style>${BASE_STYLE}
    table { page-break-inside: auto; }
    tr { page-break-inside: avoid; page-break-after: auto; }
    thead { display: table-header-group; }
  </style></head><body>
    <div class="header">
      <div><div class="brand">CostSpace Estimation</div><div class="doc-title">BOQ Detail Report</div></div>
      <div class="meta">Generated ${nowStr}<br/>Revision ${revision.revision_number}</div>
    </div>

    <div class="info-grid">
      <div class="info-row"><span class="info-label">Project / Tender</span><span class="info-value">${tender.name}</span></div>
      <div class="info-row"><span class="info-label">Revision</span><span class="info-value">Rev ${revision.revision_number} — ${revision.label}</span></div>
      <div class="info-row"><span class="info-label">Pricing Date</span><span class="info-value">${revision.pricing_date}</span></div>
      <div class="info-row"><span class="info-label">Total Items</span><span class="info-value">${items.length}</span></div>
    </div>

    <table>
      <thead><tr><th>Item No.</th><th>Description</th><th>Unit</th><th style="text-align:right">Quantity</th><th>Status</th><th style="text-align:right">Direct Cost</th><th style="text-align:right">Direct + Markup Price</th><th style="text-align:right">Allocated Indirect</th><th style="text-align:right">Fully Loaded Cost</th><th style="text-align:right">Fully Loaded Selling Price</th><th style="text-align:right">Final Unit Rate</th></tr></thead>
      <tbody>
        ${rowsHtml}
        <tr class="total-row"><td colspan="2">TOTAL</td><td></td><td></td><td></td><td class="num">${fmt(totalDirect)}</td><td class="num">${fmt(totalSelling)}</td><td class="num">${fmt(totalAllocatedIndirect)}</td><td class="num">${fmt(totalFullyLoadedCost)}</td><td class="num">${fmt(totalFullyLoadedSelling)}</td><td></td></tr>
      </tbody>
    </table>

    <p style="font-size:9px;color:#9CA3AF;margin-top:8px;font-style:italic;">
      Allocated Indirect / Fully Loaded Cost / Fully Loaded Selling Price / Final Unit Rate are a Phase 1 derived
      presentation (General Site Overhead + HO Overhead + General Contingency allocated by direct-cost
      proportion). They do not replace or change the official Direct + Markup Price or Tender Price above.
    </p>

    <div class="footer"><span>CostSpace Estimation — Confidential</span><span>Page 1</span></div>
  </body></html>`

  openPrintWindow(html)
}
